import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal } from "../components/motion";
import { EditorialMarquee } from "../components/EditorialMarquee";
import { SkillsMarquee } from "../components/SkillsMarquee";
import { KineticText } from "../components/KineticText";
import { Seo } from "../components/Seo";
import { useTheme } from "../context/ThemeContext";
import { HERO, METRICS, MANIFESTO, TESTIMONIAL, CASE_STUDIES, MEDIA } from "../data/content";

const HeroMedia = ({ y }) => {
  const { colorMode } = useTheme();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (colorMode !== "dark" || HERO.videos.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO.videos.length), 9000);
    return () => clearInterval(t);
  }, [colorMode]);

  return (
    <motion.div style={{ y }} aria-hidden className="absolute inset-0 -z-10 overflow-hidden">
      {colorMode === "dark" ? (
        <div className="absolute inset-0" data-testid="hero-video-carousel">
          {HERO.videos.map((src, i) => (
            <motion.video
              key={src}
              className="absolute inset-0 w-full h-full object-cover"
              animate={{ opacity: i === idx ? 0.55 : 0 }}
              transition={{ duration: 1.4, ease: "easeInOut" }}
              autoPlay muted loop playsInline preload="auto"
              poster={MEDIA.heroImageLight}
            >
              <source src={src} type="video/mp4" />
            </motion.video>
          ))}
        </div>
      ) : (
        <img src={MEDIA.heroImageLight} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.4 }} />
      )}
      {/* readability scrim */}
      <div className="absolute inset-0" style={{ background: "linear-gradient(100deg, var(--bg) 0%, color-mix(in srgb, var(--bg) 85%, transparent) 38%, color-mix(in srgb, var(--bg) 30%, transparent) 100%)" }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, var(--bg), transparent 45%)" }} />
      <div className="absolute right-[-8%] top-[12%] h-[46vh] w-[46vh] rounded-full blur-[130px]" style={{ background: "var(--accent-soft)" }} />
    </motion.div>
  );
};

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yParallax = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        keywords="Yuva Kumar, UX Director, AI-Native Product Designer, UX Strategy, Design Systems Governance, Design Ops, Agentic AI, Enterprise UX, APAC"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Yuva Kumar — UX Director",
            url: "https://yuvadesign.in/",
          },
          {
            "@context": "https://schema.org",
            "@type": "Person",
            name: "Yuva Kumar",
            alternateName: "Yuvakumar",
            jobTitle: "UX Director & AI-Native Product Designer",
            url: "https://yuvadesign.in/",
            email: "mailto:yuva.kumar.design@gmail.com",
            sameAs: ["https://linkedin.com/in/yuvap"],
            address: { "@type": "PostalAddress", addressLocality: "Chennai", addressCountry: "IN" },
            knowsAbout: ["UX Strategy", "Design Systems Governance", "Agentic AI in Design", "Design Ops", "Enterprise Product Design", "Design Leadership"],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              { "@type": "Question", name: "Who is Yuva Kumar?", acceptedAnswer: { "@type": "Answer", text: "Yuva Kumar is a Director of User Experience and AI-Native Product Designer with 20+ years scaling design organizations across APAC for global enterprise brands." } },
              { "@type": "Question", name: "What does Yuva Kumar specialize in?", acceptedAnswer: { "@type": "Answer", text: "UX strategy, design systems governance, design ops, team building, and embedding Generative & Agentic AI into the product design lifecycle." } },
              { "@type": "Question", name: "Which brands has Yuva Kumar worked with?", acceptedAnswer: { "@type": "Answer", text: "PepsiCo, Cartier, Verizon, Telefónica, TPG Telecom, SBI, Mahindra, Intel, Nokia, Reebok, Oreo, Mobily and more across luxury, telecom, CPG, banking and enterprise." } },
              { "@type": "Question", name: "How can I contact Yuva Kumar?", acceptedAnswer: { "@type": "Answer", text: "Use the contact form on this site or email yuva.kumar.design@gmail.com." } },
            ],
          },
        ]}
      />
      {/* ================= HERO ================= */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-28 pb-16">
        <HeroMedia y={yParallax} />

        <div className="mx-auto max-w-[1600px] w-full px-6 sm:px-10">
          <motion.div style={{ opacity }}>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="eyebrow mb-6"
            >
              Director of User Experience
            </motion.p>

            <KineticText
              className="display font-bold text-[12.5vw] sm:text-[9.2vw] lg:text-[7.6vw] max-w-[15ch] cursor-default"
              lines={[
                [{ text: "Bridging human" }],
                [{ text: "behavior with" }],
                [{ text: "business metrics.", accent: true, italic: true }],
              ]}
            />

            {/* CTAs — high, above the fold */}
            <Reveal delay={0.55} className="mt-9 flex flex-col sm:flex-row gap-3">
              {HERO.ctas.map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  data-testid={`hero-cta-${c.primary ? "primary" : "secondary"}`}
                  className="group inline-flex items-center gap-4 px-8 py-4 rounded-full border transition-colors duration-300"
                  style={{
                    background: c.primary ? "var(--accent)" : "transparent",
                    color: c.primary ? "var(--bg)" : "var(--text)",
                    borderColor: c.primary ? "var(--accent)" : "var(--line)",
                  }}
                >
                  <span className="text-sm uppercase tracking-widest">{c.label}</span>
                  <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>
              ))}
            </Reveal>

            {/* crisp, visual story */}
            <div className="mt-10 grid gap-5 sm:grid-cols-3 max-w-4xl">
              {HERO.story.map((s, i) => (
                <Reveal key={i} delay={0.7 + i * 0.12}>
                  <div className="pl-4 border-l-2" style={{ borderColor: "var(--accent)" }}>
                    <p className="text-base leading-relaxed">
                      <span className="font-semibold" style={{ color: "var(--text)" }}>{s.lead}</span>
                      <span style={{ color: "var(--muted-t)" }}>{s.rest}</span>
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= SKILLS MARQUEE ================= */}
      <SkillsMarquee />

      {/* ================= EXECUTIVE SUMMARY / METRICS ================= */}
      <section className="py-24 sm:py-32" data-testid="metrics-section">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
          <Reveal className="max-w-3xl mb-16">
            <p className="eyebrow mb-5">Executive Summary</p>
            <h2 className="display text-5xl sm:text-7xl" style={{ color: "var(--text)" }}>
              Impact, <span className="italic" style={{ color: "var(--accent)" }}>measured.</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed" style={{ color: "var(--muted-t)" }}>
              Two decades translating human insight into board-level outcomes — the numbers that define a career spent scaling design where it matters most.
            </p>
          </Reveal>

          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-6">
            {/* Spotlight image panel */}
            <Reveal>
              <div className="relative overflow-hidden h-full min-h-[440px]" style={{ borderRadius: "2px", border: "1px solid var(--line)" }}>
                <img src={MEDIA.summaryImage} alt="UX practice at work" loading="lazy" className="absolute inset-0 w-full h-full object-cover" style={{ filter: "grayscale(0.35) contrast(1.05)" }} />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.72) 4%, rgba(0,0,0,0.25) 55%, transparent)" }} />
                <div className="absolute top-6 left-6 px-4 py-2 rounded-full text-xs uppercase tracking-widest backdrop-blur-md" style={{ background: "color-mix(in srgb, var(--bg) 55%, transparent)", color: "var(--text)", border: "1px solid var(--line)" }}>
                  APAC · Enterprise & Consulting
                </div>
                <div className="absolute bottom-8 left-8 right-8">
                  <p className="display text-3xl sm:text-4xl leading-tight" style={{ color: "#fff" }}>Craft that scales — and shows up on the P&amp;L.</p>
                </div>
              </div>
            </Reveal>

            {/* Cohesive metric grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {METRICS.map((m, i) => (
                <Reveal key={i} delay={i * 0.08} className="h-full">
                  <div
                    className="group relative h-full p-8 sm:p-9 flex flex-col justify-between min-h-[210px] overflow-hidden surface-card"
                    style={{ borderRadius: "var(--radius-card)" }}
                    data-testid={`metric-card-${i}`}
                  >
                    {/* animated accent top line */}
                    <span className="absolute top-0 left-0 h-[3px] w-full origin-left scale-x-[0.25] group-hover:scale-x-100 transition-transform duration-500" style={{ background: "var(--accent)" }} />
                    <span className="display text-5xl sm:text-6xl" style={{ color: "var(--accent)" }}>{m.value}</span>
                    <div className="mt-6">
                      <p className="text-base font-medium" style={{ color: "var(--text)" }}>{m.label}</p>
                      <p className="text-xs mt-1.5" style={{ color: "var(--muted-t)" }}>{m.sub}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* ================= MANIFESTO ================= */}
      <section className="py-24 sm:py-32" data-testid="manifesto-section">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
          <Reveal><p className="eyebrow mb-16">The Operating Philosophy</p></Reveal>
          <div className="space-y-px">
            {MANIFESTO.map((m, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="grid md:grid-cols-[120px_1fr] gap-6 md:gap-16 py-10 border-t hairline group">
                  <span className="display text-3xl" style={{ color: "var(--accent)" }}>{m.n}</span>
                  <div className="grid md:grid-cols-[1fr_1.4fr] gap-4 md:gap-16">
                    <h3 className="display text-3xl sm:text-5xl" style={{ color: "var(--text)" }}>{m.title}</h3>
                    <p className="text-base leading-relaxed self-center" style={{ color: "var(--muted-t)" }}>{m.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PREVIEW GRID ================= */}
      <section className="py-24 sm:py-32" style={{ background: "var(--surface)" }} data-testid="projects-preview">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
          <Reveal className="flex items-end justify-between mb-14 flex-wrap gap-4">
            <div>
              <p className="eyebrow mb-4">Selected Work</p>
              <h2 className="display text-4xl sm:text-6xl" style={{ color: "var(--text)" }}>Case Studies</h2>
            </div>
            <Link to="/case-studies" data-testid="view-all-cases" className="group flex items-center gap-2 text-sm uppercase tracking-widest" style={{ color: "var(--accent)" }}>
              View all <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {CASE_STUDIES.map((cs, i) => (
              <Reveal key={cs.slug} delay={i * 0.1}>
                <Link to={`/case-studies/${cs.slug}`} data-testid={`preview-card-${cs.slug}`} className="group block">
                  <div className="overflow-hidden mb-5" style={{ borderRadius: "2px", border: "1px solid var(--line)" }}>
                    <div className="aspect-[4/3] overflow-hidden relative">
                      <img src={cs.image} alt={cs.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" style={{ filter: "grayscale(0.3) contrast(1.05)" }} />
                      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, color-mix(in srgb, var(--bg) 55%, transparent), transparent 55%)" }} />
                    </div>
                  </div>
                  <span className="eyebrow">{cs.tag}</span>
                  <h3 className="display text-2xl sm:text-3xl mt-2 group-hover:opacity-70 transition-opacity" style={{ color: "var(--text)" }}>{cs.title}</h3>
                  <p className="text-sm mt-2 line-clamp-2" style={{ color: "var(--muted-t)" }}>{cs.summary}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TESTIMONIAL ================= */}
      <section className="py-28 sm:py-40" data-testid="testimonial-section">
        <div className="mx-auto max-w-[1200px] px-6 sm:px-10 text-center">
          <Reveal>
            <span className="display text-7xl block leading-none mb-6" style={{ color: "var(--accent)" }}>&ldquo;</span>
            <blockquote className="display text-3xl sm:text-5xl leading-tight" style={{ color: "var(--text)" }}>
              {TESTIMONIAL.quote}
            </blockquote>
            <div className="mt-10 flex items-center justify-center gap-3">
              <span className="h-px w-10" style={{ background: "var(--accent)" }} />
              <span className="text-sm uppercase tracking-widest" style={{ color: "var(--muted-t)" }}>{TESTIMONIAL.author} — {TESTIMONIAL.role}</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= CTA BAND ================= */}
      <section className="py-24" data-testid="home-cta-band">
        <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
          <Reveal>
            <div className="surface-card p-10 sm:p-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10" style={{ background: "var(--accent-soft)", border: "1px solid var(--accent)" }}>
              <h2 className="display text-4xl sm:text-6xl max-w-[16ch]" style={{ color: "var(--text)" }}>
                Let&apos;s turn ambiguous problems into measurable outcomes.
              </h2>
              <Link to="/contact" data-testid="cta-band-link" className="group shrink-0 flex items-center gap-4 px-8 py-5 rounded-full" style={{ background: "var(--accent)", color: "var(--bg)" }}>
                <span className="text-sm uppercase tracking-widest">Let&apos;s Talk Business</span>
                <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
